import React from 'react';
import { Loader, Pagination, Select, UnstyledButton } from '@mantine/core';
import type { AdminKdsAuditSortKey, AdminKdsAuditView } from '../../../services/admin-kds-audit/admin-kds-audit.service';
import {
  KDS_AUDIT_ORDER_STATUS_LABELS,
  KDS_AUDIT_SORT_LABELS,
  KITCHEN_STATUS_LABELS,
  ORDER_TYPE_LABELS,
} from '../monitoring-page.constants';
import { kdsStatusClassName } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type KdsAuditTableProps = Pick<
  UseMonitoringPage,
  | 'kdsAuditRows'
  | 'isKdsAuditLoading'
  | 'hasLoadedKdsAudit'
  | 'kdsAuditSortKey'
  | 'kdsAuditSortDirection'
  | 'toggleKdsAuditSort'
  | 'kdsAuditTotalPages'
  | 'kdsAuditPage'
  | 'setKdsAuditPage'
  | 'openKdsHistoryModal'
  | 'getKdsDraftForLine'
  | 'applyAndSaveKdsStatus'
  | 'savingKdsRowKey'
  | 'kdsRowKey'
>;

export const KdsAuditTable: React.FC<KdsAuditTableProps> = ({
  kdsAuditRows,
  isKdsAuditLoading,
  hasLoadedKdsAudit,
  kdsAuditSortKey,
  kdsAuditSortDirection,
  toggleKdsAuditSort,
  kdsAuditTotalPages,
  kdsAuditPage,
  setKdsAuditPage,
  openKdsHistoryModal,
  getKdsDraftForLine,
  applyAndSaveKdsStatus,
  savingKdsRowKey,
  kdsRowKey,
}) => {
  const isFromInlineEditor = (target: EventTarget | null) =>
    target instanceof HTMLElement && Boolean(target.closest('[data-stop-row-open="true"]'));
  const suppressRowOpenRef = React.useRef(false);
  const suppressRowOpenOnce = () => {
    suppressRowOpenRef.current = true;
    window.setTimeout(() => {
      suppressRowOpenRef.current = false;
    }, 400);
  };

  const renderSortableHeader = (key: AdminKdsAuditSortKey) => {
    const isActive = kdsAuditSortKey === key;
    const indicator = !isActive ? '↕' : kdsAuditSortDirection === 'asc' ? '↑' : '↓';

    return (
      <UnstyledButton
        className={isActive ? `${classes.sortButton} ${classes.sortButtonActive}` : classes.sortButton}
        onClick={() => toggleKdsAuditSort(key)}
        data-testid={`monitoring-kds-sort-${key}`}
      >
        <span>{KDS_AUDIT_SORT_LABELS[key]}</span>
        <span className={classes.sortIndicator} aria-hidden="true">
          {indicator}
        </span>
      </UnstyledButton>
    );
  };

  if (isKdsAuditLoading && !hasLoadedKdsAudit) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{renderSortableHeader('companyName')}</th>
            <th>{renderSortableHeader('sessionName')}</th>
            <th>{renderSortableHeader('orderCode')}</th>
            <th>{renderSortableHeader('tableName')}</th>
            <th>{renderSortableHeader('productName')}</th>
            <th>{'כמות'}</th>
            <th>{renderSortableHeader('kitchenStatus')}</th>
            <th>{renderSortableHeader('orderStatus')}</th>
            <th>{renderSortableHeader('createdByName')}</th>
            <th>{renderSortableHeader('modifiedByName')}</th>
          </tr>
        </thead>
        <tbody>
          {kdsAuditRows.map((row) => {
            const draft = getKdsDraftForLine(row);
            const rowKey = kdsRowKey(row);
            return (
              <tr
                key={rowKey}
                className={classes.clickableRow}
                role="button"
                tabIndex={0}
                data-testid={`monitoring-kds-row-${rowKey}`}
                onClick={(event) => {
                  if (suppressRowOpenRef.current || isFromInlineEditor(event.target)) {
                    return;
                  }
                  openKdsHistoryModal(row);
                }}
                onKeyDown={(event) => {
                  if (suppressRowOpenRef.current || isFromInlineEditor(event.target)) {
                    return;
                  }
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openKdsHistoryModal(row);
                  }
                }}
              >
              <td data-label={'חברה'}>{row.companyName}</td>
              <td data-label={'משמרת'}>{row.sessionName}</td>
              <td data-label={'הזמנה'}>{row.orderCode}</td>
              <td data-label={'שולחן'}>{row.tableName ?? '-'}</td>
              <td data-label={'מוצר'}>
                <span>{row.productName}</span>
                {row.groupName && <span className={classes.kdsGroupName}>{row.groupName}</span>}
              </td>
              <td data-label={'כמות'}>{row.quantity}</td>
              <td data-label={'סטטוס'}>
                {row.isCancelled ? (
                  <div className={classes.cellEditor}>
                    <span className={`${classes.kdsStatus} ${classes.kdsStatusCancelled}`}>{'בוטל'}</span>
                  </div>
                ) : (
                  <div className={classes.cellEditor} data-stop-row-open="true">
                    <Select
                      value={draft.status}
                      onMouseDown={(event) => {
                        suppressRowOpenOnce();
                      }}
                      onClick={(event) => {
                        suppressRowOpenOnce();
                        event.stopPropagation();
                      }}
                      onKeyDown={(event) => {
                        suppressRowOpenOnce();
                      }}
                      onChange={(value) => {
                        suppressRowOpenOnce();
                        if (!value) return;
                        applyAndSaveKdsStatus(row, value as AdminKdsAuditView['kitchenStatus']);
                      }}
                      data={Object.entries(KITCHEN_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
                      allowDeselect={false}
                      size="xs"
                      disabled={savingKdsRowKey === rowKey}
                      data-testid={`monitoring-kds-row-status-${rowKey}`}
                    />
                    <span className={kdsStatusClassName(classes, row.kitchenStatus)}>
                      {row.kitchenStatus ? KITCHEN_STATUS_LABELS[row.kitchenStatus] : '-'}
                    </span>
                  </div>
                )}
              </td>
              <td data-label={'סטטוס הזמנה'}>
                {`${ORDER_TYPE_LABELS[row.orderType]} · ${KDS_AUDIT_ORDER_STATUS_LABELS[row.orderStatus]}`}
              </td>
              <td data-label={'נוצר ע״י'}>{row.createdByName ?? '-'}</td>
              <td data-label={'שונה ע״י'}>{row.modifiedByName ?? '-'}</td>
              </tr>
            );
          })}
          {kdsAuditRows.length === 0 && (
            <tr>
              <td colSpan={10} className={classes.empty}>
                {'אין רשומות מטבח המתאימות לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {kdsAuditTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={kdsAuditTotalPages} value={kdsAuditPage} onChange={setKdsAuditPage} size="sm" />
        </div>
      )}
    </div>
  );
};
